import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Input } from "@/components/_atoms/input";
import { Label } from "@/components/_atoms/label";
import { Switch } from "@/components/_atoms/switch";
import { OwnerTypes } from "@/domain/_shared/lib/owner-types";
import { capitalizeFirstLetter } from "@/lib/capitalizeFirstLetter";

import { useUpdateStoredView } from "../../hooks/api/use-update-stored-view";
import { StoredView } from "../../models/stored-view-model";
import { TeamStoredViewInput, WorkspaceStoredViewInput } from "../../models/stored-view-type";

interface StoredViewEditFormProps {
  initialData: StoredView;
  canBeShared?: TeamStoredViewInput | WorkspaceStoredViewInput | null | undefined;
  onError?: (error: Error) => void;
  onSuccess?: (data: StoredView) => void;
  onCancel?: () => void;
}

export const StoredViewEditForm: React.FC<StoredViewEditFormProps> = ({
  initialData,
  canBeShared,
  onSuccess,
  onError,
  onCancel,
}) => {
  const { t } = useTranslation("common");
  const [name, setName] = useState(initialData.name);
  const [isDefault, setIsDefault] = useState(initialData.default);
  const [isShared, setIsShared] = useState(initialData.ownerType !== "USER");

  const { updateStoredView, loading: updateStoredViewLoading } = useUpdateStoredView({
    onError: (error) => {
      onError?.(error);
    },
  });

  // Make sure form is reset when initialData changes
  useEffect(() => {
    setName(initialData.name);
    setIsDefault(initialData.default);
    setIsShared(initialData.ownerType === OwnerTypes.TEAM || initialData.ownerType === "WORKSPACE");
  }, [initialData]);

  const handleUpdateStoredView = async (e: React.FormEvent) => {
    e.preventDefault(); // Prevent form from submitting and refreshing the page

    if (updateStoredViewLoading) {
      return;
    }

    // only set ownerConfig if the view is newly being shared
    const ownerConfig =
      canBeShared && initialData.ownerType === "USER" && isShared === true
        ? {
            ownerType: canBeShared.ownerType,
            ownerId: canBeShared.ownerId,
          }
        : undefined;

    updateStoredView(
      {
        id: initialData.id,
        name: name,
        default: isDefault,
        ...ownerConfig,
      },
      {
        onSuccess: (updatedStoredView) => {
          toast.success(t("view.updateSuccess"), { description: t("view.updateSuccessDescription") });
          onSuccess?.(updatedStoredView);
        },
        onError: () => {
          toast.error(t("view.updateError"), { description: t("view.updateErrorDescription") });
        },
      },
    );
  };

  return (
    <form onSubmit={handleUpdateStoredView} className="space-y-4 p-4">
      <div className="space-y-2">
        <Label htmlFor="name" className="text-sm font-medium">
          {t("fields.name")}
        </Label>
        <Input
          id="name"
          value={name}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
          className="w-full"
          placeholder={t("view.namePlaceholder")}
        />
      </div>

      {canBeShared && (
        <div className="flex items-center gap-2">
          <Switch
            id="edit-view-shared"
            size="sm"
            checked={isShared}
            onCheckedChange={setIsShared}
            disabled={initialData.ownerType !== "USER"}
            className="relative cursor-pointer rounded-full bg-muted transition-colors data-[state=checked]:bg-primary"
          ></Switch>
          {initialData.ownerType === "USER" && (
            <Label htmlFor="edit-view-shared" className="text-sm font-medium">
              {t("view.shareWithOwner", { ownerType: capitalizeFirstLetter(canBeShared.ownerType) })}
            </Label>
          )}
          {initialData.ownerType !== "USER" && (
            <Label htmlFor="edit-view-shared" className="text-sm font-medium">
              {t("view.sharedWithOwner", { ownerType: capitalizeFirstLetter(canBeShared.ownerType) })}
            </Label>
          )}
        </div>
      )}

      <div className="flex items-center gap-2">
        <Switch
          id="edit-view-default"
          size="sm"
          checked={isDefault}
          onCheckedChange={setIsDefault}
          className="relative cursor-pointer rounded-full bg-muted transition-colors data-[state=checked]:bg-primary"
        ></Switch>
        <Label htmlFor="edit-view-default" className="text-sm font-medium">
          {canBeShared && isShared ? (
            <>{t("view.setAsDefaultForOwner", { ownerType: capitalizeFirstLetter(canBeShared.ownerType) })}</>
          ) : (
            <>{t("view.setAsDefault")}</>
          )}
        </Label>
      </div>

      <div className="border-t pt-4">
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" size="xs" onClick={onCancel} disabled={updateStoredViewLoading} className="px-4">
              {t("view.cancel")}
            </Button>
          )}
          <Button type="submit" size="xs" disabled={!name || updateStoredViewLoading} className="px-4">
            {updateStoredViewLoading ? t("view.updating") : t("view.update")}
          </Button>
        </div>
      </div>
    </form>
  );
};
